import { styled } from '@linaria/react';
import { Switch as MantineSwitch } from '@mantine/core';
import type { ReactElement } from 'react';

/** Visual size of the switch — "lg" for section headers, "sm" for dense rows. */
type SwitchSize = 'sm' | 'lg';

interface SwitchProps {
  /** Whether the switch is on. */
  on: boolean;
  /** Invoked when the user flips the switch. */
  onToggle: () => void;
  /** Visual size — "lg" for section/bucket headers, "sm" for capability rows. */
  size?: SwitchSize;
  /** Renders the switch dimmed and non-interactive. */
  disabled?: boolean;
  /** Accessible label for the switch. */
  ariaLabel: string;
  /** Id of the element that describes the switch (e.g. a setting's description). */
  ariaDescribedBy?: string;
}

/**
 * Render the house on/off switch. Wraps Mantine `Switch` and paints the checked
 * track with the secondary brand color; Mantine handles sizing and thumb
 * movement, so only the checked color is overridden.
 *
 * @param {SwitchProps} props - Switch state, toggle handler, size, disabled flag, and accessible label and description
 * @returns {ReactElement} The toggle switch
 * @example
 * <Switch on={enabled} onToggle={() => setEnabled((prev) => !prev)} size="lg" ariaLabel="Enable web search" />
 */
export function Switch({
  on,
  onToggle,
  size = 'sm',
  disabled = false,
  ariaLabel,
  ariaDescribedBy,
}: SwitchProps): ReactElement {
  return (
    <StyledSwitch
      size={size === 'lg' ? 'md' : 'sm'}
      checked={on}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      onChange={() => onToggle()}
    />
  );
}

const StyledSwitch = styled(MantineSwitch)`
  input:checked + .mantine-Switch-track {
    background: var(--secondary-base);
    border-color: var(--secondary-base);
  }
`;

export type { SwitchProps, SwitchSize };
